import { searchNewHashtag, selectExistingHashtag } from "actions/sourceMedia";
import {
  DEFAULT_HASHTAG_FILTER,
  PAGES,
  WEEKLY_HASHTAG_SEARCH_MAX,
} from "constants/MediaLibrary";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getSelectedIgAccount } from "selectors/hashtagFeedSelectors";
import { CollectionFilesList } from "features/media-library/components/collection-files-list";
import { HashtagSelector } from "features/media-library/components/media-picker/hashtag/hashtag-selector";
import { PickerProps } from "features/media-library/components/media-picker/media-picker";
import { CollectionFile } from "shared/types/files";
import { LoadingCover } from "ui/cover";
import moment from "moment";
import { Title } from "../components/common";
import { Hashtag } from "../types";
import { feedMediaToCollectionFile } from "../helper";
import { AccountSelector } from "../components/account-selector";
import { Modal } from "@/components/modal/modal";
import { SkedButton, TooltipWrapper } from "ui";
import { BUTTON_TYPES } from "constants/SkedButton";
import ShareIcon from "assets/icons/share.svg";
import { LoadMore } from "../components/load-more";
import { useHashtagFeed } from "components/MediaLibrary/SourceMedia/HashtagFeed/HashtagFeed";
import { useHashtags } from "components/MediaLibrary/LibrarySidebar/SourceMediaSidebar/HashtagSidebar/HashtagSidebar";
import { UpsellWrap, UpsellUGCCTA } from "../creator/creator-picker";
import { getUser } from "selectors/commonSelectors";
import { hasUgcLibrary } from "utils/feature-gates";

export function HashtagPicker(props: PickerProps) {
  const dispatch = useDispatch();
  const { feedMedia, selectedIgAccount, loading } = useHashtagFeed();
  const { activeHashtags, expiredHashtags, selectedHashtag, loadingHashtags } =
    useHashtags();
  const [showExpiredListModal, setShowExpiredListModal] = useState(false);

  const files: CollectionFile[] = (feedMedia || []).map(
    feedMediaToCollectionFile
  );

  const onHashtagChanged = React.useCallback(
    (hashtagId: string) => {
      if (!selectedIgAccount?._id) {
        return;
      }

      dispatch(
        selectExistingHashtag(selectedIgAccount._id, hashtagId, {
          ...DEFAULT_HASHTAG_FILTER,
          excludeAccounts: [selectedIgAccount._id],
        })
      );
    },
    [selectedIgAccount?._id]
  );

  React.useEffect(() => {
    if (
      !loading &&
      !loadingHashtags &&
      !selectedHashtag &&
      activeHashtags?.length > 0
    ) {
      onHashtagChanged(activeHashtags[0]?.hashtag_id);
    }
  }, [
    loading,
    loadingHashtags,
    selectedHashtag,
    activeHashtags,
    onHashtagChanged,
  ]);

  const user = useSelector(getUser);
  const upsell = !hasUgcLibrary(user);

  if (upsell) {
    return (
      <UpsellWrap>
        <strong>1. Add the hashtag you want to track</strong>
        <p>You can track up to 30 hashtags at a time</p>

        <strong>2. Check your results</strong>
        <p>
          See all public posts with the hashtag, and sort by recent or top posts
        </p>

        <strong>3. Select the posts you want to add</strong>
        <p>
          Add one or multiple posts to a Library Collection. Your images are
          automatically tagged by Al so you can find them later
        </p>

        <strong>4. Post your new UGC</strong>
        <p>
          Schedule your UGC content to Instagram, Pinterest, Facebook, Twitter
          Linkedln, or Google Business Profile. Automatically saved metadata
          makes sure you're acknowledging the original creator
        </p>
        <UpsellUGCCTA />
      </UpsellWrap>
    );
  }

  return (
    <LoadingCover isLoading={loading || loadingHashtags}>
      <div className="flex items-center gap-3 my-5">
        <AccountSelector type="hashtag" />
        <HashtagSelector
          hashtags={activeHashtags}
          account={selectedIgAccount}
          openExpiredListModal={() => setShowExpiredListModal(true)}
          showExpiredListModal={expiredHashtags.length > 0}
          onHashtagChanged={onHashtagChanged}
        />
        <div className="flex items-center gap-1.5 font-inter text-xs text-neutral-400">
          <span>Active hashtags:</span>
          <span className="font-medium text-neutral-600">
            {activeHashtags.length}/{WEEKLY_HASHTAG_SEARCH_MAX}
          </span>
          <TooltipWrapper
            tooltip="30 hashtags per Instagram account in a 7 day rolling period"
            placement="bottom"
          >
            <i className="fa-regular fa-circle-info text-neutral-400 text-xs cursor-help" />
          </TooltipWrapper>
        </div>
      </div>

      {expiredHashtags.length > 0 && (
        <Modal
          show={showExpiredListModal}
          onHide={() => setShowExpiredListModal(false)}
          data-testid="expired-hashtags-model"
          bsStyle="giselle"
          className="mt-[100px]"
        >
          <Modal.Header closeButton className="bg-white">
            <Modal.Title className="text-neutral-800">
              Expired hashtags
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-sm leading-[17px] text-neutral-700">
              Instagram limits users to 30 hashtags on a rolling 7 day period.
              Click the preview icon to make sure that you're choosing the right
              tag before reactivating.
            </p>
            <ExpiredHashtagsList
              hashtags={expiredHashtags}
              onChange={() => setShowExpiredListModal(false)}
            />
            <SkedButton
              size="small"
              type={BUTTON_TYPES.ALERT_OUTLINE}
              onClick={() => setShowExpiredListModal(false)}
              className="mt-5"
            >
              Cancel
            </SkedButton>
          </Modal.Body>
        </Modal>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12 min-h-[200px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-3"></div>
          <p className="font-inter text-sm text-neutral-600">
            Loading hashtag posts...
          </p>
        </div>
      ) : !selectedHashtag ? (
        expiredHashtags.length > 0 ? (
          <div className="w-[436px] min-h-[215px] mx-auto my-[100px]">
            <h3 className="text-center text-base leading-[19px] text-neutral-800 font-bold">
              Search for hashtags
            </h3>
            <p className="text-sm leading-[17px] text-center text-neutral-900 mt-5">
              Search for a new hashtag or select from a previously used hashatg.
              30 hashtags can be searched. and each will expire after 7 days.
            </p>
            <ExpiredHashtagsList hashtags={expiredHashtags} />
          </div>
        ) : (
          <EmptyAlert />
        )
      ) : files.length === 0 ? (
        <div className="text-center py-12 max-w-md mx-auto">
          <i className="fa-regular fa-hashtag text-4xl text-neutral-300 mb-4"></i>
          <h3 className="font-gotham font-semibold text-lg mb-2 text-neutral-900">
            No posts found
          </h3>
          <p className="font-inter text-sm text-neutral-600 mb-4">
            No posts found for #{selectedHashtag.name}. Try a different hashtag.
          </p>
        </div>
      ) : (
        <>
          <Title>
            {`#${selectedHashtag.name}`}{" "}
            <span>Results from the last 24 hours. Expires in 7 days</span>
          </Title>
          <CollectionFilesList
            files={files}
            selectedMediaSet={props.selectedMediaFiles}
            selectedCollection={props.selectedCollection}
            onSelect={props.handleFileAdd}
            onRemove={props.handleFileRemove}
          />
          {files.length > 0 && <LoadMore page={PAGES.HASHTAGS} />}
        </>
      )}
    </LoadingCover>
  );
}

function EmptyAlert() {
  return (
    <div className="text-center py-12 max-w-md mx-auto">
      <i className="fa-regular fa-hashtag text-4xl text-neutral-300 mb-4"></i>
      <h3 className="font-gotham font-semibold text-lg mb-2 text-neutral-900">
        Search for a hashtag
      </h3>
      <p className="font-inter text-sm text-neutral-600 mb-4">
        Find Instagram posts from specific hashtags to add to your library
      </p>
      <ol className="text-left inline-block font-inter text-sm space-y-2 text-neutral-700">
        <li>1. Select an Instagram account above</li>
        <li>2. Search and add a hashtag</li>
        <li>3. Browse posts and click to select</li>
      </ol>
      <div className="mt-4 font-inter text-xs text-neutral-500">
        <p>• Up to 30 hashtags can be tracked</p>
        <p>• Results from the last 24 hours</p>
        <p>• Each hashtag expires after 7 days</p>
      </div>
    </div>
  );
}

function ExpiredHashtagsList({
  hashtags,
  onChange,
}: {
  hashtags: Hashtag[];
  onChange?: () => void;
}) {
  const dispatch = useDispatch();
  const account = useSelector(getSelectedIgAccount);

  const onReactivate = (hashtag: Hashtag) => {
    if (!account) {
      return;
    }

    dispatch(
      searchNewHashtag(account._id, hashtag.name.toLowerCase(), "picker")
    );
    onChange?.();
  };
  return (
    <div>
      {(hashtags || []).map((hashtag) => {
        const expiredDate = hashtag.search_date
          ? moment().to(moment(hashtag.search_date).add(7, "days"), true)
          : "Unknown";

        return (
          <div
            key={hashtag.hashtag_id}
            className="flex border-b border-neutral-200 py-2"
          >
            <div className="flex-[0_0_33.333%] text-xs leading-[14px] flex items-center text-neutral-800 w-[135px] overflow-hidden">
              #{hashtag.name}
            </div>
            <div className="flex-[0_0_33.333%] text-xs leading-[14px] flex items-center text-right text-neutral-400">
              Expired {expiredDate} ago
            </div>
            <div className="flex-[0_0_33.333%] flex justify-end items-center">
              <a
                target="_blank"
                rel="norefferrer"
                href={`https://www.instagram.com/explore/tags/${hashtag.name}`}
                className="mr-2.5 mt-0.5"
              >
                <ShareIcon />
              </a>
              <button
                onClick={() => onReactivate(hashtag)}
                className="text-[10px] leading-3 font-medium underline flex items-center text-right text-blue-500 cursor-pointer bg-transparent border-none"
              >
                Reactivate
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// All styled-components have been converted to Tailwind CSS classes above
